import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { FormDialogConfig } from '../../stores/dialogStore';
import { LoadingButton } from '../shared/ui/Loading';

interface FormDialogProps {
  config: FormDialogConfig;
  onClose: () => void;
}

function FormDialog({ config, onClose }: FormDialogProps) {
  const [isProcessing, setIsProcessing] = useState(false);

  const handleSubmit = async () => {
    setIsProcessing(true);
    try {
      await config.onSubmit();
      onClose();
    } catch (error) {
      // Handle step transition errors silently (these are expected)
      if (error instanceof Error && error.message === 'STEP_TRANSITION') {
        // Don't close dialog, just stop processing
        return;
      }

      // Handle validation errors silently
      if (error instanceof Error && error.message === 'VALIDATION_FAILED') {
        // Don't close dialog, just stop processing
        return;
      }

      // Error in form submit - handled silently
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancel = (_event?: object, reason?: string) => {
    // disableEscapeKeyDown removed from MUI Dialog in v9; block escape here.
    if (config.disableEscapeKeyDown && reason === 'escapeKeyDown') {
      return;
    }
    if (config.onCancel) {
      config.onCancel();
    }
    onClose();
  };

  return (
    <Dialog
      open={config.open}
      onClose={handleCancel}
      maxWidth={config.maxWidth || 'md'}
      fullWidth={config.fullWidth !== false}
      disableEnforceFocus
      slotProps={{
        paper: {
          sx: {
            maxHeight: '80vh',
            display: 'flex',
            flexDirection: 'column',
          },
        },
      }}
      aria-labelledby="form-dialog-title"
    >
      {config.rawContent ? (
        // When rawContent is true, render content directly without wrapping
        config.content
      ) : (
        // Default mode: wrap content with our own DialogTitle and DialogContent
        <>
          <DialogTitle id="form-dialog-title">
            <Typography variant="h5" className="mb-2">
              {config.title}
            </Typography>
          </DialogTitle>

          <DialogContent
            sx={{
              flex: '1 1 auto',
              overflow: 'auto',
              display: 'flex',
              flexDirection: 'column',
              maxHeight: '60vh',
            }}
          >
            {config.content}
          </DialogContent>

          {/* Only render DialogActions if submit or cancel text is provided */}
          {((config.submitText && config.submitText.trim() !== '') ||
            (config.cancelText && config.cancelText.trim() !== '')) && (
            <DialogActions sx={{ justifyContent: 'space-between' }}>
              {/* Left side: Extra actions */}
              <div>{config.extraActions}</div>

              {/* Right side: Cancel and Submit buttons */}
              <div style={{ display: 'flex', gap: '8px' }}>
                {config.cancelText && config.cancelText.trim() !== '' && (
                  <Button
                    onClick={handleCancel}
                    color="primary"
                    variant="outlined"
                    disabled={isProcessing}
                  >
                    {config.cancelText}
                  </Button>
                )}

                {config.submitText && config.submitText.trim() !== '' && (
                  <LoadingButton
                    onClick={handleSubmit}
                    color="primary"
                    variant="contained"
                    disabled={Boolean(isProcessing || config.submitDisabled)}
                    loading={isProcessing}
                    showExternalSpinner
                  >
                    {config.submitText}
                  </LoadingButton>
                )}
              </div>
            </DialogActions>
          )}
        </>
      )}
    </Dialog>
  );
}

export default FormDialog;
